<template>
  <div class="event-correspondence">

    <!--Loading-->
    <div v-if="isLoading" class="ec-loading text-center">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <!--Empty: no linked emails-->
    <div v-else-if="loadError" class="ec-card ec-empty text-center text-danger">
      <i class="fas fa-exclamation-circle mr-1"></i>Couldn't load correspondence. <a href="#" @click.prevent="loadData">Retry</a>
    </div>
    <div v-else-if="items.length === 0" class="ec-card ec-empty text-center">
      <i class="fas fa-envelope ec-empty-icon"></i>
      <div class="ec-empty-title">No correspondence linked to this event yet</div>
      <div class="ec-empty-text">
        Emails that include this event's number are linked automatically, or you can link one manually from the Correspondence mailbox.
      </div>
      <router-link to="/correspondence" class="ec-link">Go to Correspondence &rarr;</router-link>
    </div>

    <div v-else class="ec-layout">

      <!--LEFT: filters-->
      <div class="ec-card ec-filters">
        <div class="ec-group-label">Filter by category</div>
        <label class="ec-check">
          <input type="checkbox" :checked="selectedCategories.length === 0" @change="clearCategories">
          <span class="ec-check-label">All categories</span>
          <span class="ec-count">{{ categoryCounts.total }}</span>
        </label>
        <label v-for="category in senderCategories" :key="category.id" class="ec-check">
          <input type="checkbox" :checked="isCategorySelected(category.id)" @change="toggleCategory(category.id)">
          <span class="ec-check-label"><span class="category-dot" :class="getDotClass(category.id)"></span>{{ category.label }}</span>
          <span class="ec-count">{{ categoryCounts.byCategory[category.id] || 0 }}</span>
        </label>
        <label v-if="categoryCounts.hasUncategorized" class="ec-check">
          <input type="checkbox" :checked="isCategorySelected(uncategorizedKey)" @change="toggleCategory(uncategorizedKey)">
          <span class="ec-check-label"><span class="category-dot dot-uncategorized"></span>Uncategorized</span>
          <span class="ec-count">{{ categoryCounts.uncategorized }}</span>
        </label>

        <div class="ec-group-label mt-3">Status</div>
        <label class="ec-check">
          <input type="checkbox" v-model="hideResolved">
          <span class="ec-check-label">Hide resolved emails</span>
        </label>
      </div>

      <!--RIGHT: list-->
      <div class="ec-main">
        <div class="ec-header">
          <span class="text-muted">{{ filteredItems.length }} linked {{ filteredItems.length === 1 ? 'email' : 'emails' }}</span>
          <router-link :to="{ path: '/correspondence', query: { eventId: eventId } }" class="ec-link">Open full mailbox in Correspondence &rarr;</router-link>
        </div>

        <div v-if="filteredItems.length === 0" class="ec-card ec-empty-filter text-center text-muted">
          No correspondence matches these filters.
        </div>

        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="ec-card ec-email"
          :class="{ open: isExpanded(item.id), resolved: item.resolved }">
          <div
            class="ec-email-header"
            role="button"
            tabindex="0"
            :aria-expanded="isExpanded(item.id) ? 'true' : 'false'"
            :aria-controls="'ec-body-' + item.id"
            @click="toggleExpanded(item.id)"
            @keydown.enter.prevent="toggleExpanded(item.id)"
            @keydown.space.prevent="toggleExpanded(item.id)">
            <div class="ec-email-main">
              <div class="ec-tags">
                <span class="ec-pill category-chip" :class="getCategoryClass(item.categoryId)">
                  <span class="category-dot" :class="getDotClass(item.categoryId)"></span>{{ getCategoryLabel(item.categoryId) }}
                </span>
                <span v-if="isAutoLinked(item)" class="ec-pill link-auto">Auto-linked</span>
                <span v-else-if="isManualLinked(item)" class="ec-pill link-manual">Manually linked</span>
                <span v-if="item.resolved" class="ec-pill ec-resolved">Resolved</span>
              </div>
              <div class="ec-subject">{{ item.subject }}</div>
              <div class="ec-snippet text-muted">{{ getSnippet(item.body) }}</div>
            </div>
            <div class="ec-email-meta">
              <div class="ec-from">{{ item.fromName || item.fromAddress }}</div>
              <div class="ec-date text-muted">{{ item.dateUtc | formatDate }}</div>
            </div>
            <i class="fas fa-chevron-down ec-chevron" :class="{ rotated: isExpanded(item.id) }"></i>
          </div>
          <div v-if="isExpanded(item.id)" :id="'ec-body-' + item.id" class="ec-email-body">
            <div class="email-body" v-html="safeBody(item.body)"></div>
            <div class="ec-email-footer">
              <span class="text-muted">{{ item.fromAddress }}</span>
              <router-link :to="{ path: '/correspondence', query: { id: item.id } }" class="ec-link">View full thread in Correspondence &rarr;</router-link>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import {
  UNCATEGORIZED_KEY,
  isAutoLinked,
  isManualLinked,
  filterItems,
  computeCategoryCounts,
  makeSnippet
} from '@/utils/eventCorrespondenceFilters'
import { sanitizeEmailHtml } from '@/utils/sanitizeEmailHtml'

// Same colorKey set as Correspondence.vue
var categoryColorKeys = ['insurance', 'vendor', 'employee', 'witness', 'person', 'tool']

export default {
  name: 'EventCorrespondenceTab',
  props: {
    eventId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      items: [],
      senderCategories: [],
      isLoading: false,
      loadError: false,
      selectedCategories: [],
      hideResolved: false,
      expandedIds: [],
      uncategorizedKey: UNCATEGORIZED_KEY
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    ...mapActions('correspondence', ['getCorrespondence', 'getSenderCategories']),
    async loadData () {
      var self = this
      self.isLoading = true
      self.loadError = false
      try {
        var results = await Promise.all([self.getSenderCategories(), self.getCorrespondence({ eventId: self.eventId })])
        var categoriesResult = results[0]
        var correspondenceResult = results[1]
        self.senderCategories = (categoriesResult && categoriesResult.data && categoriesResult.data.senderCategories) ? categoriesResult.data.senderCategories : []
        if (!correspondenceResult || !correspondenceResult.data) {
          self.loadError = true
        }
        self.items = (correspondenceResult && correspondenceResult.data && correspondenceResult.data.correspondence) ? correspondenceResult.data.correspondence : []
      } catch (e) {
        self.items = []
        self.loadError = true
      } finally {
        self.isLoading = false
      }
    },
    safeBody: function (body) {
      return sanitizeEmailHtml(body)
    },
    isAutoLinked (item) {
      return isAutoLinked(item)
    },
    isManualLinked (item) {
      return isManualLinked(item)
    },
    getSnippet (body) {
      return makeSnippet(body, 160)
    },
    clearCategories () {
      this.selectedCategories = []
    },
    isCategorySelected (key) {
      return this.selectedCategories.indexOf(key) !== -1
    },
    toggleCategory (key) {
      var index = this.selectedCategories.indexOf(key)
      if (index === -1) {
        this.selectedCategories.push(key)
      } else {
        this.selectedCategories.splice(index, 1)
      }
    },
    isExpanded (id) {
      return this.expandedIds.indexOf(id) !== -1
    },
    toggleExpanded (id) {
      var index = this.expandedIds.indexOf(id)
      if (index === -1) {
        this.expandedIds.push(id)
      } else {
        this.expandedIds.splice(index, 1)
      }
    },
    findCategory (categoryId) {
      var found = null
      this.senderCategories.forEach(function (category) {
        if (category.id === categoryId) {
          found = category
        }
      })
      return found
    },
    getCategoryLabel (categoryId) {
      var category = this.findCategory(categoryId)
      if (category) {
        return category.label
      }
      return 'Uncategorized'
    },
    getCategoryClass (categoryId) {
      var category = this.findCategory(categoryId)
      if (category && categoryColorKeys.indexOf(category.colorKey) !== -1) {
        return 'category-' + category.colorKey
      }
      return 'category-uncategorized'
    },
    getDotClass (categoryId) {
      var category = this.findCategory(categoryId)
      if (category && categoryColorKeys.indexOf(category.colorKey) !== -1) {
        return 'dot-' + category.colorKey
      }
      return 'dot-uncategorized'
    }
  },
  computed: {
    filteredItems () {
      return filterItems(this.items, this.selectedCategories, this.hideResolved)
    },
    categoryCounts () {
      return computeCategoryCounts(this.items, this.hideResolved)
    }
  }
}
</script>

<style scoped>
.ec-email-header:focus {
  outline: 2px solid #4671e0;
  outline-offset: 2px;
}
.event-correspondence {
  background-color: #f4f6f8;
  padding: 18px;
  border-radius: 7px;
}
.ec-loading {
  padding: 3em 0;
}
.ec-layout {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 18px;
  align-items: start;
}
.ec-card {
  background-color: #ffffff;
  border: 1px solid #e3e6ea;
  border-radius: 7px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}
.ec-filters {
  padding: 14px 16px;
}
.ec-group-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7686;
  font-weight: 700;
  letter-spacing: .04em;
  margin-bottom: 8px;
}
.ec-check {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 5px 0;
  cursor: pointer;
  font-size: 13px;
  color: #2f3a48;
}
.ec-check input {
  margin-right: 8px;
  cursor: pointer;
}
.ec-check-label {
  flex-grow: 1;
  display: flex;
  align-items: center;
}
.ec-count {
  color: #6b7686;
  font-size: 12px;
  font-weight: 600;
}
.ec-main {
  min-width: 0;
}
.ec-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 13px;
}
.ec-link {
  font-weight: 600;
  font-size: 13px;
}
.ec-email {
  margin-bottom: 10px;
  overflow: hidden;
}
.ec-email.resolved .ec-subject {
  color: #6b7686;
}
.ec-email-header {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  cursor: pointer;
}
.ec-email-header:hover {
  background-color: #fafbfc;
}
.ec-email-main {
  flex-grow: 1;
  min-width: 0;
}
.ec-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.ec-pill {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  border-radius: 999px;
  padding: 2px 8px;
}
.link-auto {
  background-color: #E3F3EF;
  color: #146B5C;
}
.link-manual {
  background-color: #E8EDF5;
  color: #35507A;
}
.ec-resolved {
  background-color: #eceff1;
  color: #6c757d;
}
.ec-subject {
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2933;
}
.ec-snippet {
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ec-email-meta {
  text-align: right;
  margin-left: 16px;
  white-space: nowrap;
  font-size: 12.5px;
}
.ec-from {
  font-weight: 600;
  color: #2f3a48;
}
.ec-date {
  font-size: 12px;
}
.ec-chevron {
  margin-left: 12px;
  margin-top: 3px;
  color: #6b7686;
  transition: transform .15s ease;
}
.ec-chevron.rotated {
  transform: rotate(180deg);
}
.ec-email-body {
  padding: 4px 16px 12px;
  border-top: 1px solid #eef0f3;
}
.email-body {
  line-height: 1.5;
  padding: 10px 0;
  font-size: 13.5px;
  overflow-x: auto;
}
.ec-email-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px dashed #d8dde3;
  padding-top: 10px;
  font-size: 12.5px;
}
.ec-empty {
  padding: 3em 2em;
}
.ec-empty-icon {
  font-size: 2.2em;
  color: #9aa5b1;
  margin-bottom: 12px;
}
.ec-empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #2f3a48;
  margin-bottom: 6px;
}
.ec-empty-text {
  font-size: 13px;
  color: #6b7686;
  max-width: 460px;
  margin: 0 auto 14px;
}
.ec-empty-filter {
  padding: 2em;
  font-size: 13px;
}

/* Category colors (copied from Correspondence.vue) */
.category-chip {
  background-color: #f5f5f5;
  color: #666666;
}
.category-uncategorized {
  background-color: #f0f1f2;
  color: #6c757d;
}
.category-insurance {
  background-color: #ece4f7;
  color: #6a3ea1;
}
.category-vendor {
  background-color: #fde3cc;
  color: #b5601a;
}
.category-employee {
  background-color: #e5f2ff;
  color: #00518d;
}
.category-witness {
  background-color: #fbdcdc;
  color: #b3282f;
}
.category-person {
  background-color: #dcf1e3;
  color: #1f7a41;
}
.category-tool {
  background-color: #dde7ee;
  color: #33627e;
}
.category-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}
.dot-insurance {
  background-color: #6a3ea1;
}
.dot-vendor {
  background-color: #b5601a;
}
.dot-employee {
  background-color: #00518d;
}
.dot-witness {
  background-color: #b3282f;
}
.dot-person {
  background-color: #1f7a41;
}
.dot-tool {
  background-color: #33627e;
}
.dot-uncategorized {
  background-color: #b0b7bd;
}

@media (max-width: 767.98px) {
  .ec-layout {
    grid-template-columns: 1fr;
  }
  .ec-email-header {
    flex-wrap: wrap;
  }
  .ec-email-meta {
    text-align: left;
    margin-left: 0;
    margin-top: 6px;
  }
}
</style>
